<template>
  <notification-inbox
    :loading="loading"
    :notifications="notifications"
    :selected-notification="selectedNotification"
    :current-page="pagination.currentPage"
    :total-pages="pagination.size"
    :unread-count="unreadCount"
    :categories="categories"
    :active-category="activeCategory"
    :unread-only="unreadOnly"
    :theme="theme"
    :voted="selectedNotification ? !!votedById[selectedNotification.id] : false"
    :voting-busy="votingBusy"
    :dispute-answered="
      selectedNotification ? !!resolvedById[selectedNotification.id] : false
    "
    :dispute-busy="disputeBusy"
    :extra-lessons-preview="extraLessons.preview"
    :realign-busy="realignBusy"
    :extra-lessons-busy="extraLessons.busy"
    :extra-lessons-incomplete="extraLessons.incomplete"
    :extra-lessons-decision="extraLessonsDecision"
    :title="$t('inbox.title')"
    :subtitle="$t('notificationsPage.partnerSubtitle')"
    @select="openNotification"
    @category-change="changeCategory"
    @toggle-read="toggleRead"
    @unread-only-change="changeUnreadOnly"
    @vote="vote"
    @resolve-dispute="resolveDispute"
    @preview-extra-lessons="previewExtraLessons"
    @confirm-extra-lessons="confirmExtraLessons"
    @decline-extra-lessons="declineExtraLessons"
    @realign-subjects="realignSubjects"
    @reset-extra-lessons="resetExtraLessons"
    @page-change="getNotifications"
  />
</template>

<script>
import NotificationInbox from "@/components/notifications/NotificationInbox.vue";
import { classThemes } from "@/shared/classThemes";
import partnerApi from "@/shared/pitchPartnerApi";

export default {
  name: "NotificationsArea",

  components: {
    NotificationInbox,
  },

  data() {
    return {
      notifications: [],
      categories: [],
      activeCategory: null,
      unreadOnly: false,
      selectedId: null,
      loading: true,
      votingBusy: false,
      votedById: {},
      disputeBusy: false,
      resolvedById: {},
      // Prévia das aulas extras vive aqui, não no componente: a caixa de
      // entrada é compartilhada com o aluno e não fala com a API.
      extraLessons: { preview: null, busy: false, incomplete: false },
      realignBusy: false,
      unreadCount: 0,
      pagination: {
        currentPage: 1,
        size: 1,
      },
    };
  },

  computed: {
    selectedNotification() {
      return (
        this.notifications.find((item) => item.id === this.selectedId) || null
      );
    },
    theme() {
      return classThemes.partner;
    },
    // Card 86ajzet19 (retorno de 15/09): o estado da pergunta vem da API junto
    // com a notificação. Guardado só na memória da tela, ele se perdia ao
    // recarregar e os botões voltavam para uma pergunta já respondida.
    extraLessonsDecision() {
      const state = this.selectedNotification?.extraLessons;
      return state && state.status !== "PENDING" ? state : null;
    },
  },

  async mounted() {
    // Card 86akj8ptp: o box de notificações da página inicial abre a
    // notificação clicada (?id=). A primeira página já traz as fixadas e as
    // mais recentes -- as mesmas que o box mostra.
    const wanted = this.$route.query?.id || null;
    await this.getNotifications(1, wanted);
    const item = wanted
      ? this.notifications.find((entry) => entry.id === wanted)
      : null;
    if (item) this.openNotification(item);
  },

  methods: {
    async getNotifications(
      page = this.pagination.currentPage,
      preferredId = null
    ) {
      this.loading = true;
      this.pagination.currentPage = page;

      try {
        const res = await partnerApi.getNotifications(
          page,
          this.activeCategory,
          this.unreadOnly
        );
        this.notifications = res.data?.notifications || [];
        this.categories = res.data?.categories || [];
        this.pagination.size = Math.max(
          1,
          Math.ceil((res.data?.total || 0) / 10)
        );
        // Total de não lidas do professor, não só as desta página: a lista é
        // paginada de 10 em 10 e o contador ficaria errado a partir da 2ª.
        this.unreadCount =
          res.data?.unread ??
          this.notifications.filter((item) => !item.read).length;
        this.syncSelection(preferredId);
      } catch {
        this.$root.$emit("display-alert", {
          text: this.$t("notificationsPage.loadFailed"),
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    syncSelection(preferredId = null) {
      if (!this.notifications.length) {
        this.selectedId = null;
        return;
      }

      const match = this.notifications.find((item) => item.id === preferredId);
      this.selectedId = (match || this.notifications[0]).id;
    },
    // Trocar de aba é uma busca nova no servidor: volta para a primeira
    // página, senão a aba abre numa página que a categoria filtrada não tem.
    changeCategory(category) {
      if (category === this.activeCategory) return;

      this.activeCategory = category;
      this.selectedId = null;
      this.resetExtraLessons();
      this.getNotifications(1);
    },
    // Retorno de 30/10: o destinatário também marca como lida ou não lida na
    // mão. Abrir a notificação continua marcando como lida sozinho.
    async toggleRead(item) {
      const wasRead = !!item.read;

      try {
        if (wasRead) await partnerApi.unreadNotification(item.id);
        else await partnerApi.readNotification(item.id);

        item.read = !wasRead;
        const delta = wasRead ? 1 : -1;
        this.unreadCount = Math.max(0, this.unreadCount + delta);
        const entry = this.categories.find((c) => c.key === item.category);
        if (entry) entry.unread = Math.max(0, entry.unread + delta);
      } catch {
        this.$root.$emit("display-alert", {
          text: this.$t("notificationsPage.readFailed"),
          type: 1,
        });
      }
    },
    // O filtro "não lidas" é do servidor, como a categoria: volta ao começo.
    changeUnreadOnly(value) {
      if (value === this.unreadOnly) return;

      this.unreadOnly = value;
      this.selectedId = null;
      this.resetExtraLessons();
      this.getNotifications(1);
    },
    discountCategory(key) {
      const entry = this.categories.find((item) => item.key === key);
      if (entry) entry.unread = Math.max(0, entry.unread - 1);
    },
    async openNotification(item) {
      this.selectedId = item.id;

      if (!item.read) {
        try {
          await partnerApi.readNotification(item.id);
          item.read = true;
          // Badge da aba e total descem junto, sem recarregar a página.
          this.unreadCount = Math.max(0, this.unreadCount - 1);
          this.discountCategory(item.category);
        } catch {
          this.$root.$emit("display-alert", {
            text: this.$t("notificationsPage.readFailed"),
            type: 1,
          });
        }
      }
    },
    async vote({ voteId, option }) {
      const currentId = this.selectedId;
      this.votingBusy = true;

      try {
        await partnerApi.vote(voteId, option);
        this.$set(this.votedById, currentId, true);
        this.$root.$emit("display-alert", {
          text: this.$t("notificationsPage.voted"),
          type: 0,
        });
        await this.getNotifications(this.pagination.currentPage, currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.voteClosed"),
          type: 1,
        });
      } finally {
        this.votingBusy = false;
      }
    },
    // Decisão sobre a discordância de um aluno. "Vou remarcar" só registra a
    // decisão e avisa o aluno — a remarcação em si é feita pela agenda.
    resetExtraLessons() {
      this.extraLessons = { preview: null, busy: false, incomplete: false };
    },
    async previewExtraLessons({ classId, quantity }) {
      this.extraLessons.busy = true;

      try {
        const res = await partnerApi.previewExtraLessons(classId, quantity);
        this.extraLessons.preview = res.data?.slots || [];
        this.extraLessons.incomplete = !!res.data?.incomplete;

        // Sem nenhuma data livre não há prévia para confirmar; dizer o
        // motivo evita o professor achar que o botão não funcionou.
        if (!this.extraLessons.preview.length) {
          this.extraLessons.preview = null;
          this.$root.$emit("display-alert", {
            text: this.$t("notificationsPage.noFreeDate"),
            type: 1,
          });
        }
      } catch (err) {
        this.extraLessons.preview = null;
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.previewFailed"),
          type: 1,
        });
      } finally {
        this.extraLessons.busy = false;
      }
    },
    async confirmExtraLessons({ classId, quantity, promptId }) {
      const currentId = this.selectedId;
      this.extraLessons.busy = true;

      try {
        const res = await partnerApi.createExtraLessons(classId, {
          quantity,
          promptId,
        });
        this.resetExtraLessons();
        this.$root.$emit("display-alert", {
          text: this.$tc(
            "notificationsPage.extraCreated",
            res.data?.created || quantity,
            { count: res.data?.created || quantity }
          ),
          type: 0,
        });
        await this.getNotifications(this.pagination.currentPage, currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.extraFailed"),
          type: 1,
        });
        // Pergunta respondida em outra aba ou encerrada por uma mais nova
        // (409): recarrega para a notificação mostrar o que já foi decidido.
        if (err?.response?.status === 409) {
          this.resetExtraLessons();
          await this.getNotifications(this.pagination.currentPage, currentId);
        }
      } finally {
        this.extraLessons.busy = false;
      }
    },
    async realignSubjects({ classId, eventId }) {
      this.realignBusy = true;

      try {
        const { data } = await partnerApi.realignSubjects(classId, eventId);
        const changed = Number(data?.changed || 0);
        const removed = Number(data?.removed || 0);

        this.$root.$emit("display-alert", {
          text:
            changed || removed
              ? this.$t("notificationsPage.realignDone", { changed, removed })
              : this.$t("notificationsPage.realignNothing"),
          type: 0,
        });
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.realignFailed"),
          type: 1,
        });
      } finally {
        this.realignBusy = false;
      }
    },
    async declineExtraLessons({ promptId }) {
      const currentId = this.selectedId;
      this.extraLessons.busy = true;

      try {
        await partnerApi.declineExtraLessons(promptId);
        await this.getNotifications(this.pagination.currentPage, currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.answerFailed"),
          type: 1,
        });
      } finally {
        this.extraLessons.busy = false;
      }
    },
    async resolveDispute({ disputeId, reschedule, response }) {
      const currentId = this.selectedId;
      this.disputeBusy = true;

      try {
        await partnerApi.resolveClassChangeDispute(disputeId, {
          reschedule,
          response,
        });
        this.$set(this.resolvedById, currentId, true);
        this.$root.$emit("display-alert", {
          text: reschedule
            ? this.$t("notificationsPage.rescheduleDecision")
            : this.$t("notificationsPage.keepDecision"),
          type: 0,
        });
        await this.getNotifications(this.pagination.currentPage, currentId);
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message ||
            this.$t("notificationsPage.decisionFailed"),
          type: 1,
        });
      } finally {
        this.disputeBusy = false;
      }
    },
  },
};
</script>
